import React, { useContext, useEffect } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { UserInfo } from '@optimizely/react-sdk/dist/utils'
import { Column, Sheet } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'

import useMarketplaceDisclaimer from '@/components/layouts/main-layout/hooks/use-marketplace-disclaimer'
import InternationalizationContext from '@/context/internationalization/context'
import { ListingProvider } from '@/context/listing'
import ListingsContext from '@/context/listings'
import { setRecentlyViewedLocalStorageById } from '@/context/listings/utils'
import MapContext from '@/context/map'
import productionDetailsContext from '@/context/production-details'
import userDataContext from '@/context/user-data/context'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { useLocalizedCurrencyFeature } from '@/optimizely/features/internationalization/hooks/localized-currency'
import { useDesktopMediaQuery } from '@/utils'

import { getRefererIsExternal } from '../../utils'
import filterContext from '../context'

import MapSeatView from './components/map-seat-view'
import TicketInformation from './components/ticket-information'
import ticketDetailsContext from './context'
import styles from './styles.module.scss'

type TicketDetailsProps = { setUser?: (user: UserInfo | undefined) => void; referer?: string } & WithOptimizelyProps

const TicketDetails: React.FC<TicketDetailsProps> = ({ optimizely, setUser, referer }) => {
    const isDesktop = useDesktopMediaQuery()
    const router = useRouter()

    const {
        selectors: { productionVenueInfo },
    } = useContext(productionDetailsContext)

    const {
        state: { listing, listingGroup },
        dispatch: { toggleDetails },
    } = useContext(ticketDetailsContext)

    const { state: filterState } = useContext(filterContext)
    const { selectors: listingsSelectors } = useContext(ListingsContext)
    const { seatViewObject, isFaceValue, listingsInSelectedSection } = listingsSelectors
    const listingsInSection = listingsInSelectedSection(listing?.sectionId)

    const {
        state: { priceGroupId },
    } = useContext(userDataContext)

    const {
        selectors: { isMapboxMapEnabled, venueMapState },
    } = useContext(MapContext)
    const {
        selectors: { selectedCurrency },
    } = useContext(InternationalizationContext)

    const [isLocalizedCurrencyFeatureEnabled] = useLocalizedCurrencyFeature()

    function resetMapBoxMapView() {
        if (isMapboxMapEnabled) {
            venueMapState?.onMapResetClick({ resetFilters: false })
            venueMapState?.onMapRevertTicketSelectionZoom()
        }
    }

    /* istanbul ignore next */
    useEffect(() => {
        if (!listing) return

        const user = cloneDeep(optimizely?.user)
        optimizely?.track(OPTIMIZELY_EVENT.productionTicketDetailsView)

        if (productionVenueInfo?.productionId) {
            setRecentlyViewedLocalStorageById(listing.id, productionVenueInfo?.productionId)
            listing.isRecentlyViewed = true
        }

        if (!optimizely?.user) return

        if (user?.attributes) {
            user.attributes = {
                ...user.attributes,
                ticket_details_visitor: !!listing,
            }
            if (!isEqual(user, optimizely.user) && setUser) {
                setUser(user)
            }
        }
    }, [listing, productionVenueInfo?.productionId])
    const { desktopDisclaimerTypes } = useMarketplaceDisclaimer()

    const backButtonHandler = () => {
        const refererIsExternal = getRefererIsExternal(referer)
        resetMapBoxMapView()
        if (refererIsExternal) toggleDetails({ listing: null })
        else router.back()
    }
    // when the user is in listing detail view and clicks on a row kick them out of listing details
    useEffect(() => {
        if (!listing || !isMapboxMapEnabled) return
        backButtonHandler()
    }, [filterState.selectedRowsFilterState.length, isMapboxMapEnabled])

    // This sets top-offset for the sliding ticket information pane to one of the fixed values (see _tokens.scss) depending on the banner and header heights
    // Fixed top-offset is required so that ticket information pane does not overlap with the banner and the header
    // See src/components/layouts/main-layout/components/marketplace-disclaimer/component.tsx and src/components/layouts/main-layout/components/marketplace-disclaimer/styles.module.scss
    const desktopSheetClassName = classNames(styles.sheet, {
        [styles.withDisclaimer1Line]: desktopDisclaimerTypes.length === 1,
        [styles.withDisclaimer2Lines]: desktopDisclaimerTypes.length === 2,
        [styles.withDisclaimer3Lines]: desktopDisclaimerTypes.length === 3,
    })

    // this zooms into the mapbox map when a user lands on a listing detail page
    useEffect(() => {
        if (!listing || !isMapboxMapEnabled || !venueMapState?.mapMetadataLoaded) return
        venueMapState?.onMapSectionClickFromListing(listing.sectionId || '', listing.mbi || '')
    }, [isMapboxMapEnabled, venueMapState?.mapMetadataLoaded])

    // router back button behavior
    useEffect(() => {
        router.beforePopState((state) => {
            if (state.url !== router.asPath) {
                resetMapBoxMapView()
            }
            return true
        })
        return () => {
            router.beforePopState(() => {
                return true
            })
        }
    }, [resetMapBoxMapView])

    return (
        <Sheet
            toggle={!!listing}
            roundedSize="sm"
            enterFrom={isDesktop ? 'left' : 'right'}
            className={desktopSheetClassName}
            data-testid="ticket-details-sheet"
        >
            <div className={styles.ticketDetailsContainer} data-testid="ticket-details-container">
                <div className={styles.mapSeatView}>
                    <MapSeatView
                        onBackButtonClick={backButtonHandler}
                        sectionId={listing?.sectionId}
                        jsonFileName={seatViewObject?.jsonFileName}
                        staticMapUrl={seatViewObject?.staticMapUrl}
                        seatView={seatViewObject?.ticketSections[listing?.sectionId || '']?.section?.si || ''}
                    />
                </div>
                <Column noFlex className={styles.ticketInformation}>
                    {listing && productionVenueInfo && (
                        <ListingProvider
                            initialParams={{
                                listingId: listing.id,
                                productionId: productionVenueInfo.productionId,
                                priceGroupId,
                                currency: isLocalizedCurrencyFeatureEnabled ? selectedCurrency : '',
                                includeIpAddress: true,
                            }}
                        >
                            <TicketInformation
                                productionVenueInfo={productionVenueInfo}
                                listing={listing}
                                listingGroup={listingGroup ?? undefined}
                                isFaceValue={isFaceValue}
                                price={
                                    filterState.showAllInPrice && listing.allInPrice
                                        ? listing.allInPrice
                                        : listing.price
                                }
                                showAllInPricing={filterState.showAllInPrice}
                                showAllTicketsInSection={
                                    !filterState.hasSectionBeenManuallyFiltered && listingsInSection.length > 1
                                }
                                setUser={(user) => {
                                    if (setUser) setUser(user)
                                }}
                            />
                        </ListingProvider>
                    )}
                </Column>
            </div>
        </Sheet>
    )
}

export default withOptimizely(TicketDetails)
